import {
  KoobTable,
  KoobTableHeader,
  KoobTableBody,
  KoobTableRow,
  KoobTableHead,
  KoobTableCell,
  KoobSegmentedControl,
  KoobSegmentedControlItem,
} from '@koob/ui';
import ReceiptContentItem from './ReceiptContentItem';
import { KoobTitle } from '@koob/ui';
import { useMemo } from 'react';
import ReceiptContentManualItem from './ReceiptContentManualItem';
import ReceiptContentTotal from './ReceiptContentTotal';
import ReceiptContentMargin from './ReceiptContentMargin';
import { useTranslation } from 'react-i18next';
import { ReceiptTypeEnum } from './ReceiptTypeEnum';
import { buildPricingDisplay, formatCurrency } from '@koob/utils';
import React from 'react';
import ReceiptCurrencyConversion from './ReceiptCurrencyConversion';

const DISPLAY_MODES = [
  { id: 'total', label: 'receipt.displayModes.total' },
  { id: 'category', label: 'receipt.displayModes.category' },
  { id: 'itemized', label: 'receipt.displayModes.itemized' },
];

const defaultCurrencyConversion = {
  currencies: [],
  targetCurrency: null,
  exchangeRate: 1.0,
  disabled: false,
};

export default function ReceiptContent({
  items = [],
  setItems,
  markup,
  setMarkup,
  displayMode = 'itemized',
  setDisplayMode,
  type = ReceiptTypeEnum.TRIP,
  currencyConversion = defaultCurrencyConversion,
  onUpdatePricingSummary,
}) {
  const { t } = useTranslation('trips');

  const targetCurrency = currencyConversion?.targetCurrency;
  const exchangeRate = currencyConversion?.exchangeRate;
  const showConvertedColumn =
    !!targetCurrency &&
    exchangeRate != null &&
    exchangeRate !== '' &&
    !isNaN(Number(exchangeRate)) &&
    Number(exchangeRate) > 0 &&
    items?.some((item) => item.convertedPrice != null);

  const sourceCurrency = items?.[0]?.currency;

  const totalColSpan = showConvertedColumn ? 7 : 6;
  const subtotalColSpan = showConvertedColumn ? 5 : 4;

  const heads = [
    { label: t('receipt.bookedItems.columnItem') },
    { label: t('receipt.bookedItems.columnDates') },
    { label: t('receipt.bookedItems.columnPax') },
    ...(showConvertedColumn
      ? [
          {
            label: t('receipt.bookedItems.columnKoobPrice'),
            export: false,
          },
          { label: t('receipt.bookedItems.markup'), export: false },
          {
            label: t('receipt.bookedItems.columnTotal'),
            align: 'right',
          },
        ]
      : [
          { label: t('receipt.bookedItems.markup'), export: false },
          {
            label: t('receipt.bookedItems.columnTotal'),
            align: 'right',
          },
        ]),
    { label: '', align: 'right' },
  ];

  const pricingDisplay = useMemo(
    () =>
      buildPricingDisplay({
        services: items,
        displayMode,
        useConvertedPrice: showConvertedColumn,
      }),
    [items, displayMode, showConvertedColumn],
  );

  const grandTotalCurrency = !showConvertedColumn
    ? items?.[0]?.currency
    : targetCurrency;

  const isBooking = type === ReceiptTypeEnum.BOOKING;
  const sectionTitle = isBooking
    ? t('receipt.bookedItems.titleBooking')
    : t('receipt.bookedItems.titleTrip');

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <KoobTitle>{sectionTitle}</KoobTitle>
        {setDisplayMode && (
          <div className="export-hidden">
            <KoobSegmentedControl
              size="sm"
              value={displayMode}
              onValueChange={setDisplayMode}
              className="border-gray"
            >
              {DISPLAY_MODES.map((mode) => (
                <KoobSegmentedControlItem key={mode.id} value={mode.id}>
                  {t(mode.label)}
                </KoobSegmentedControlItem>
              ))}
            </KoobSegmentedControl>
          </div>
        )}
      </div>

      {displayMode !== 'total' && (
        <KoobTable>
          <KoobTableHeader>
            <KoobTableRow>
              {heads.map((head, index) => (
                <KoobTableHead key={index} align={head.align}>
                  {head.export === false ? (
                    <span className="export-hidden">{head.label}</span>
                  ) : (
                    head.label
                  )}
                </KoobTableHead>
              ))}
            </KoobTableRow>
          </KoobTableHeader>

          <KoobTableBody>
            {pricingDisplay.categories.map((category) => (
              <React.Fragment key={category.id}>
                {category.items.length > 0 && (
                  <KoobTableRow>
                    <KoobTableCell colSpan={totalColSpan}>
                      <div className="uppercase tracking-wider text-xs font-semibold text-orange-600">
                        {category.label}
                      </div>
                    </KoobTableCell>
                  </KoobTableRow>
                )}
                {displayMode === 'itemized' &&
                  category.items.map((item, index) => (
                    <ReceiptContentItem
                      key={`${category.id}-${item.id}-${index}`}
                      onRemove={() =>
                        setItems((currentItems) =>
                          currentItems.filter(({ id }) => id !== item.id),
                        )
                      }
                      setAllItems={setItems}
                      item={item}
                      showConvertedColumn={showConvertedColumn}
                      targetCurrency={targetCurrency}
                    />
                  ))}
                {category.items.length > 0 && (
                  <KoobTableRow className="border-t-2 border-t-gray-300 bg-muted/30">
                    <KoobTableCell>
                      <div className="font-medium text-gray-600">
                        {category.label} subtotal
                      </div>
                    </KoobTableCell>
                    <KoobTableCell colSpan={subtotalColSpan} align="right">
                      <div className="font-medium">
                        {formatCurrency({
                          amount: category.subtotal,
                          currency: grandTotalCurrency,
                          noDecimals: showConvertedColumn,
                        })}
                      </div>
                    </KoobTableCell>
                  </KoobTableRow>
                )}
              </React.Fragment>
            ))}
          </KoobTableBody>
        </KoobTable>
      )}

      <ReceiptContentTotal
        price={pricingDisplay.total}
        currency={grandTotalCurrency}
        showConvertedColumn={showConvertedColumn}
      />

      <ReceiptContentManualItem
        setManualItems={setItems}
        currency={sourceCurrency}
      />

      <ReceiptContentMargin markup={markup} setMarkup={setMarkup} />

      {onUpdatePricingSummary && (
        <ReceiptCurrencyConversion
          sourceCurrency={sourceCurrency}
          currencies={currencyConversion.currencies.filter(
            (c) => c.value !== sourceCurrency,
          )}
          value={{
            targetCurrency: currencyConversion.targetCurrency,
            exchangeRate: currencyConversion.exchangeRate,
          }}
          onUpdate={onUpdatePricingSummary}
          disabled={currencyConversion.disabled}
        />
      )}
    </div>
  );
}
